import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FilesService } from 'src/app/services/files.service';
import { SnackService } from 'src/app/services/snack.service';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { CaseManagementService } from 'src/app/services/case-management/case-management.service';
import { ClinicalResolution } from 'src/app/models/clinical.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { PatientFiles } from 'src/app/models/patient.model';
import { take } from 'rxjs/operators';
import { FileUploadResponse } from 'src/app/models/file-upload.model';
import { getFileSizeError, getUploadErrorMessage } from 'src/app/shared/utils/file-upload.utils';

@Component({
  selector: 'app-upload-return-label-dialog',
  templateUrl: './upload-return-label-dialog.component.html',
  styleUrls: ['./upload-return-label-dialog.component.scss'],
})
export class UploadReturnLabelDialogComponent implements OnInit {
  @ViewChild('returnLabelInput') returnLabelInput: ElementRef;

  returnLabelForm: UntypedFormGroup;
  returnLabelFile: File | null = null;
  returnLabelFileName = '';

  labels: PatientFiles[] = [];
  loadingLabels = true;
  isUploadingReturnLabel = false;

  constructor(
    private fb: UntypedFormBuilder,
    private snack: SnackService,
    private filesService: FilesService,
    private auth: NewAuthService,
    private caseManagement: CaseManagementService,
    private dialogRef: MatDialogRef<UploadReturnLabelDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.returnLabelForm = this.fb.group({
      carrier: ['', Validators.required],
    });

    this.loadLabels();
  }

  loadLabels() {
    this.loadingLabels = true;

    this.filesService
      .getReturnLabelsByUserId(this.data.patientId)
      .pipe(take(1))
      .subscribe({
        next: (labels) => {
          this.labels = labels;
          this.loadingLabels = false;
        },
        error: (err) => {
          console.error('Error loading labels:', err);
          this.loadingLabels = false;

          this.snack.genericSnackBar('Error loading return labels.', ['error-snackbar'], 4000);
        },
      });
  }

  openReturnLabelFileDialog(event: Event) {
    event.preventDefault();
    this.returnLabelInput.nativeElement.click();
  }

  onReturnLabelFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg'];

    if (!validTypes.includes(file.type)) {
      this.snack.genericSnackBar('Invalid file type', ['error-snackbar']);
      return;
    }

    const sizeError = getFileSizeError(file);
    if (sizeError) {
      this.snack.genericSnackBar(sizeError, ['error-snackbar']);
      return;
    }

    this.returnLabelFile = file;
    this.returnLabelFileName = file.name;

    this.uploadReturnLabel();
  }

  async uploadReturnLabel() {
    if (this.returnLabelForm.invalid) {
      this.returnLabelForm.markAllAsTouched();
      this.snack.genericSnackBar('Please select a return carrier before uploading.', ['error-snackbar'], 3000);
      return;
    }

    if (!this.returnLabelFile) {
      return;
    }

    this.isUploadingReturnLabel = true;

    const filePath = `documents/${this.data.clientId}/${this.data.patientId}/return-labels`;

    this.filesService.uploadFile([this.returnLabelFile], filePath).subscribe({
      next: async (resp: FileUploadResponse) => {
        const url = resp.uploadedFiles[0].url;
        const carrier = this.returnLabelForm.value.carrier;
        const expiration = this.calculateReturnLabelExpirationDate(carrier);

        const metadata = {
          title: `Return Label - ${this.returnLabelFileName}`,
          url,
          carrier,
          type: 'return-label',
          return_label_expiration_date: expiration,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: {
            id: this.auth.user.user_id,
            name: `${this.auth.user.firstName} ${this.auth.user.lastName}`,
          },
          user_id: this.data.patientId,
        };

        try {
          await this.filesService.addPatientFile(metadata);
          this.createClinicalEntry(metadata);
          this.snack.genericSnackBar('Return label uploaded', ['success-snackbar']);

          this.returnLabelFile = null;
          this.returnLabelFileName = '';
          this.loadLabels();
        } finally {
          this.isUploadingReturnLabel = false;
        }
      },

      error: (err) => {
        console.error('Upload error:', err);
        this.isUploadingReturnLabel = false;
        this.snack.genericSnackBar(getUploadErrorMessage(err), ['error-snackbar']);
      },
    });
  }

  async deleteLabel(label: PatientFiles) {
    try {
      await this.filesService.removeFile(label.url).toPromise();
      await this.filesService.deletePatientFile(label.id);

      this.snack.genericSnackBar('Label deleted successfully', ['success-snackbar']);
      this.loadLabels();
    } catch (e) {
      this.snack.genericSnackBar('Error deleting label', ['error-snackbar']);
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  formatCarrier(carrier: string) {
    if (!carrier) {
      return;
    }
    return carrier.toUpperCase();
  }

  removeReturnLabel(): void {
    this.returnLabelFile = null;
    this.returnLabelFileName = '';

    if (this.returnLabelInput) {
      this.returnLabelInput.nativeElement.value = '';
    }
  }

  private calculateReturnLabelExpirationDate(carrier: string): Date {
    const date = new Date();

    switch (carrier) {
      case 'ups':
        date.setDate(date.getDate() + 120);
        break;
      case 'usps':
        date.setDate(date.getDate() + 365);
        break;
      case 'fedex':
        date.setDate(date.getDate() + 730);
        break;
      default:
        date.setDate(date.getDate() + 120);
    }

    return date;
  }

  private createClinicalEntry(meta: any) {
    const assessment = {
      associations: {
        uid: this.data.patientId,
        client_id: this.data.clientId,
      },
      assessor: this.auth.user.user_id,
      content: `A return shipping label was uploaded on ${new Date().toDateString()}`,
      category: 'return-label',
      categoryLabel: 'Return Label',
      date: new Date(),
      pinned: true,
    };

    this.caseManagement.createClinicalAssessment(assessment);
  }
}
